import { useEffect, useRef, useState } from "react";
import {
  Stack,
  Button,
  TextField,
  Typography,
  FormControl,
  CircularProgress
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { preventBubbling } from "utils/listings/helper";
import { db } from "firebaseConfig";
import { Edit, Restore } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import type { SxProps } from "@material-ui/system";
import type { ChangeEvent, FormEvent } from "react";
import type { FavoriteListingsOfContact } from "../../../@types/sharedSchema";

type ListingMatchDescriptionOverrideProps = {
  favorited: boolean;
  listingId: string;
  description: string;
  favoriteListingsOfContact: FavoriteListingsOfContact;
};

export function ListingMatchDescriptionOverride({
  favorited,
  listingId,
  description,
  favoriteListingsOfContact
}: ListingMatchDescriptionOverrideProps): JSX.Element {
  const { t: translate } = useTranslation();

  const [editMode, setEditMode] = useState(false);

  const [descriptionOverride, setDescriptionOverride] = useState("");
  const [loading, setLoading] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const { additionalDetails } = favoriteListingsOfContact ?? {};

  const rawDescriptionOverride = additionalDetails?.[listingId]?.descriptionOverride;

  const parsedDescriptionOverride = (rawDescriptionOverride || description) ?? "";

  const trimmedAdditionNotes = descriptionOverride.trim();

  const isDescriptionOverrideSameAsOriginal = description === trimmedAdditionNotes;

  const indexDescriptionOverrideFieldKey = getStringListingDescriptionOverrideIndex(listingId);

  useEffect(() => {
    setDescriptionOverride(parsedDescriptionOverride);
  }, [parsedDescriptionOverride]);

  const handleDescriptionOverrideChange = ({
    target: { value }
  }: ChangeEvent<HTMLInputElement>): void => {
    setDescriptionOverride(value);
  };

  const handleDescriptionOverrideSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    setLoading(true);

    const isTheSameAsOverride = trimmedAdditionNotes === rawDescriptionOverride;

    const skipUpdatingDescriptionOverride =
      isDescriptionOverrideSameAsOriginal || isTheSameAsOverride;

    if (skipUpdatingDescriptionOverride) {
      setLoading(false);
      setEditMode(false);
      return;
    }

    try {
      await db
        .collection("favorite-listings-of-contact")
        .doc(favoriteListingsOfContact.id)
        .update({
          [indexDescriptionOverrideFieldKey]: trimmedAdditionNotes
        });
    } catch {
      setDescriptionOverride(parsedDescriptionOverride);
    } finally {
      setLoading(false);
      setEditMode(false);
    }
  };

  const handleRestoreOriginalDescription = async (): Promise<void> => {
    const isDescriptionOverrideEmpty = !rawDescriptionOverride;

    const skipResettingDescriptionOverride =
      isDescriptionOverrideSameAsOriginal || isDescriptionOverrideEmpty;

    if (skipResettingDescriptionOverride) {
      handleCancelEditMode();
      return;
    }

    setLoading(true);
    setDescriptionOverride(description);

    try {
      await db
        .collection("favorite-listings-of-contact")
        .doc(favoriteListingsOfContact.id)
        .update({
          [indexDescriptionOverrideFieldKey]: ""
        });
    } catch (error) {
      console.error("Error resetting description override:", error);
    } finally {
      setEditMode(false);
      setLoading(false);
    }
  };

  const toggleEditMode =
    (enable: boolean = true) =>
    (): void => {
      setEditMode(enable);
    };

  const handleCancelEditMode = (): void => {
    setEditMode(false);
    setDescriptionOverride(parsedDescriptionOverride);
  };

  const handleRequestSubmit = (): void => {
    formRef.current?.requestSubmit();
  };

  const readonlyDescription = <Typography variant="body2">{parsedDescriptionOverride}</Typography>;

  if (!favorited) return readonlyDescription;

  return (
    <FormControl
      fullWidth
      ref={formRef}
      component="form"
      onSubmit={handleDescriptionOverrideSubmit}
    >
      <Stack gap={2}>
        {editMode ? (
          <TextField
            required
            fullWidth
            multiline
            size="small"
            value={descriptionOverride}
            disabled={loading}
            placeholder={translate("listingsMatch.descriptionOverride")}
            inputProps={{ sx: { resize: "none", overflow: "hidden" } }}
            InputProps={{
              endAdornment: loading && <CircularProgress size={20} />
            }}
            onClick={preventBubbling()}
            onChange={handleDescriptionOverrideChange}
          />
        ) : (
          <Typography variant="body2">{parsedDescriptionOverride}</Typography>
        )}
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={2}>
          {editMode ? (
            <Button
              sx={{
                ...defaultFullWidthStyles,
                alignSelf: "start"
              }}
              size="small"
              color="info"
              variant="contained"
              disabled={loading}
              startIcon={<Restore />}
              onClick={preventBubbling(handleRestoreOriginalDescription)}
            >
              {translate("listingsMatch.restoreOriginalDescription")}
            </Button>
          ) : (
            <Button
              sx={{ alignSelf: "start" }}
              size="small"
              color="primary"
              variant="contained"
              disabled={loading}
              startIcon={<Edit />}
              onClick={preventBubbling(toggleEditMode(true))}
            >
              {translate("listingsMatch.overrideDescription")}
            </Button>
          )}
          {editMode && (
            <Stack sx={defaultFullWidthStyles} direction="row" gap={2}>
              <Button
                sx={defaultFullWidthStyles}
                size="small"
                variant="contained"
                color="error"
                disabled={loading}
                onClick={preventBubbling(handleCancelEditMode)}
              >
                {translate("general.cancel")}
              </Button>
              <LoadingButton
                sx={defaultFullWidthStyles}
                type="submit"
                size="small"
                color="primary"
                variant="contained"
                loading={loading}
                onClick={preventBubbling(handleRequestSubmit)}
              >
                {translate("general.confirm")}
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Stack>
    </FormControl>
  );
}

const defaultFullWidthStyles: SxProps = {
  width: {
    xs: "100%",
    md: "initial"
  }
};

export function getStringListingDescriptionOverrideIndex(listingId: string): string {
  return `additionalDetails.${listingId}.descriptionOverride`;
}
